import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AppSurfaceService } from '@core/config/app-surface.service';
import { UserQuery } from '@core/store/user/user.query';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideChevronRight } from '@ng-icons/lucide';
import { V2_ICONS } from '@v2/v2.icons';
import { v2MenuSections } from '@v2/shared/v2-nav';
import { SidebarState } from '../menu.model';
import { MenuKeyboardNavigationDirective } from './menu-keyboard-navigation.directive';
import { V2RailMenuItemComponent } from './v2-rail-menu-item.component';

/**
 * The grouped desktop navigation. Ported from evo-accounts
 * `layout/sidebar/sidebar.component.ts`.
 *
 * State comes in as an input and changes go out as an event: the rail never
 * writes to `SidebarStateService` itself. The shell owns the service and is the
 * one writer, which is what keeps the app bar's toggle and the rail's own
 * controls describing a single state rather than two that have to be kept in
 * step.
 *
 * The contents are not an input. It reads `UserQuery` itself, because the shell
 * has no business knowing who is looking in order to lay out a grid. What each
 * viewer sees is decided in `v2-nav.ts`, which is a pure function pinned by
 * specs -- this component only renders what it is handed.
 */
@Component({
  selector: 'app-v2-rail',
  templateUrl: './v2-rail.component.html',
  styleUrls: ['./v2-rail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [NgIcon, V2RailMenuItemComponent, MenuKeyboardNavigationDirective],
  providers: [provideIcons({ ...V2_ICONS, lucideChevronRight })],
})
export class V2RailComponent {
  private readonly userQuery = inject(UserQuery);
  private readonly surface = inject(AppSurfaceService);

  readonly state = input.required<SidebarState>();

  readonly stateChange = output<Partial<SidebarState>>();
  readonly menuItemClick = output<{ itemId: string; sectionId: string }>();

  /**
   * `null` until the user request settles. `v2NavGroups` reads that as
   * not-an-admin, so the first paint shows nothing privileged and the admin
   * groups appear when the store fills.
   */
  private readonly user = toSignal(this.userQuery.getUser$, { initialValue: null });

  protected readonly menuSections = computed(() => v2MenuSections(this.user(), this.surface.current));

  toggleSidebar(): void {
    this.stateChange.emit({ isHidden: !this.state().isHidden });
  }

  /**
   * A new `Set` per toggle rather than a mutation of the one in the signal:
   * the state object is replaced wholesale on every write, and a `Set` that is
   * mutated in place would have readers comparing a value to itself and
   * concluding nothing changed.
   */
  toggleSection(sectionId: string): void {
    const currentState = this.state();
    const newCollapsedSections = new Set(currentState.collapsedSections);

    if (newCollapsedSections.has(sectionId)) {
      newCollapsedSections.delete(sectionId);
    } else {
      newCollapsedSections.add(sectionId);
    }

    this.stateChange.emit({ collapsedSections: newCollapsedSections });
  }

  isSectionCollapsed(sectionId: string): boolean {
    return this.state().collapsedSections.has(sectionId);
  }

  onMenuItemClick(itemId: string, sectionId: string): void {
    this.menuItemClick.emit({ itemId, sectionId });

    // evo-accounts closes the rail behind a tapped row, because below its
    // breakpoint the rail IS the drawer laid over the page. Here the shell
    // hides the rail entirely below 880px and the bottom tab bar is the
    // phone's navigation, so this can only ever fire above the breakpoint --
    // where the rail is a column beside the page and closing it would be a
    // surprise. Kept, guarded, as the one place the behaviour would go if the
    // rail ever became a drawer here too.
    if (typeof window !== 'undefined' && window.innerWidth < 880) {
      this.stateChange.emit({ isHidden: true });
    }
  }
}
